import { NavLink, Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { Breadcrumb, Button, DropdownMenu, MoreButton, PageTitle, Text } from '@ingrid/luterkort-react';

import { RoleGuard } from '~src/components/RoleGuard/RoleGuard';
import { useTranslation } from '~src/i18n/use-translation';
import { useToast } from '~src/main/hooks/useToast';
import { AppRoute } from '~src/main/routes';
import { Format } from '~src/utils/format';

import { useShipmentTemplateRemoveMutation } from '../mutations';
import { ShipmentTemplateDetails } from '../types';

interface Props {
  template: ShipmentTemplateDetails;
}

export function Header({ template }: Props) {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const removeMutation = useShipmentTemplateRemoveMutation();

  const handleRemoveTemplate = async () => {
    const promise = removeMutation.mutateAsync(template.id!);

    await toast.promise(promise, {
      loading: { title: t('shipmentTemplates.details.removeLoading') },
      success: { title: t('shipmentTemplates.details.removeSuccess') },
      error: { title: t('shipmentTemplates.details.removeError') },
    });

    navigate('..');
  };

  return (
    <PageTitle.Wrapper style={{ flexWrap: 'wrap' }}>
      <PageTitle.Stack>
        <Breadcrumb aria-label={t('common.breadcrumbs')}>
          <DropdownMenu.Root modal={false}>
            <DropdownMenu.Trigger>
              <MoreButton style={{ display: 'inline', verticalAlign: 'top' }} />
            </DropdownMenu.Trigger>

            <DropdownMenu.Content align="start" sideOffset={6} style={{ minWidth: 140, width: 'auto' }}>
              <DropdownMenu.Item asChild>
                <RouterLink to="../..">{t('common.settings')}</RouterLink>
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>

          <NavLink to="..">{t('common.template')}</NavLink>
          <span aria-current="page">{Format.Id(template.id!)}</span>
        </Breadcrumb>
      </PageTitle.Stack>

      <RoleGuard roles={['transport:edit']} withTooltip>
        <Button as={NavLink} to={`../${AppRoute.EDIT}/${id}`}>
          {t('common.edit')}
        </Button>
      </RoleGuard>

      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <MoreButton variant="secondary" />
        </DropdownMenu.Trigger>

        <DropdownMenu.Content>
          <RoleGuard roles={['transport:edit']} withTooltip>
            <DropdownMenu.Item asChild>
              <RouterLink to={`../${AppRoute.DUPLICATE}/${template.id!}`}>{t('common.duplicate')}</RouterLink>
            </DropdownMenu.Item>
          </RoleGuard>

          <RoleGuard roles={['transport:edit']} withTooltip>
            <DropdownMenu.Item onClick={handleRemoveTemplate}>
              <Text variant="critical">{t('common.delete')}</Text>
            </DropdownMenu.Item>
          </RoleGuard>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </PageTitle.Wrapper>
  );
}
